"use client";

// Session-aware fetch for the v5 quant gateway. Attaches the NextAuth signed
// HS256 Bearer; the gateway derives the RLS user id. Role, access level, and
// allowed_modules are resolved from the gateway (/api/v5/me), NOT the session
// token (the NextAuth flag can drop in the Edge runtime).
//
// Impersonation: a superadmin-minted token stored in sessionStorage under
// `mmt_impersonate` overrides the session Bearer so the whole app renders as
// the target user. Start/stop reload the page so every hook re-reads cleanly.
import { useSession } from "next-auth/react";
import { useCallback, useEffect, useState } from "react";

import { report, reportSuccess } from "@/lib/api-errors";

// Absolute API base for the Capacitor native shell / static export. On the web
// server build this returns "" so calls stay relative and hit the /api/v4
// rewrite proxy. In the native shell (or when NEXT_PUBLIC_API_URL is set at build
// time) it returns the production gateway, defaulting to https://api.mymtree.com.
function isCapacitor(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } };
  return Boolean(w.Capacitor?.isNativePlatform?.());
}
export function API_BASE(): string {
  const env = process.env.NEXT_PUBLIC_API_URL;
  if (isCapacitor()) return env || "https://api.mymtree.com";
  // web: relative (rewrite proxy). If a build explicitly forces absolute, honor it.
  return process.env.NEXT_PUBLIC_FORCE_ABS_API === "1" ? (env || "https://api.mymtree.com") : "";
}

type Me = {
  user_id: number; is_admin: boolean; modules: string[];
  access_level: string; role: string; email: string | null; impersonating: boolean;
  can_run_pipeline?: boolean; live_enabled?: boolean; max_bots?: number; predictions_daily_cap?: number;
  operating_mode?: string; hides_personal_data?: boolean;
};

const IMP_KEY = "mmt_impersonate";
function impToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.sessionStorage.getItem(IMP_KEY);
}

export function startImpersonate(token: string) {
  window.sessionStorage.setItem(IMP_KEY, token);
  window.location.assign("/mytradeos");
}
export function stopImpersonate() {
  window.sessionStorage.removeItem(IMP_KEY);
  window.location.assign("/mytradeos/admin");
}

// /api/v5/me is fetched by EVERY useApi() mount, and a dashboard page mounts
// dozens of them — the gateway saw ~20 identical /me calls per navigation.
// One in-flight promise + a short TTL per token collapses that to one call;
// the TTL stays short so an admin toggle (operating mode, entitlements)
// still lands within seconds rather than being cached into next week.
const ME_TTL_MS = 15_000;
let _meCache: { token: string; at: number; p: Promise<Me | null> } | null = null;
function fetchMeShared(token: string, doFetch: (p: string) => Promise<unknown>): Promise<Me | null> {
  const now = Date.now();
  if (_meCache && _meCache.token === token && now - _meCache.at < ME_TTL_MS) {
    return _meCache.p;
  }
  const p = doFetch("/api/v5/me").then((d) => d as Me).catch(() => {
    // a failed fetch must not poison the window — let the next mount retry
    if (_meCache?.p === p) _meCache = null;
    return null;
  });
  _meCache = { token, at: now, p };
  return p;
}

export function useApi() {
  const { data: session } = useSession();
  const user = session?.user as { id?: string; isAdmin?: boolean } | undefined;
  const sessionToken = (session as { apiToken?: string } | null)?.apiToken;
  const token = impToken() ?? sessionToken;

  const apiFetch = useCallback(
    async (path: string, init?: RequestInit) => {
      const headers = new Headers(init?.headers);
      if (token) headers.set("Authorization", `Bearer ${token}`);
      if (init?.body && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }
      // On web the /api/v4 rewrite proxies to the gateway (relative is fine). In the
      // Capacitor native shell there is no server/rewrite, so prepend the absolute
      // production API base so calls reach https://api.mymtree.com/api/v5/*.
      const url = path.startsWith("/") ? `${API_BASE()}${path}` : path;
      // Every failure is REPORTED here and still thrown. Reporting at the
      // one choke point means the 222 call sites that swallow the throw no
      // longer make the failure invisible — the banner renders even when a
      // caller falls back to null. Callers that handle their own errors are
      // unaffected: this adds a side effect, not a behaviour change.
      let res: Response;
      try {
        res = await fetch(url, { ...init, headers });
      } catch (netErr) {
        // a network/CORS failure never reaches res.ok — without this it was
        // the one class of error that vanished completely
        report(path, netErr instanceof Error ? netErr : new Error(String(netErr)));
        throw netErr;
      }
      if (!res.ok) {
        const err = new Error(`${res.status}: ${await res.text()}`);
        report(path, err);
        throw err;
      }
      reportSuccess(path);   // a desk that works clears its own banner
      return res.json();
    },
    [token],
  );

  const [me, setMe] = useState<Me | null>(null);
  useEffect(() => {
    if (!token) { setMe(null); return; }
    let alive = true;
    fetchMeShared(token, apiFetch)
      .then((d) => alive && setMe(d))
      .catch(() => alive && setMe(null));
    return () => { alive = false; };
  }, [token, apiFetch]);

  return {
    apiFetch,
    apiToken: token,
    userId: me ? String(me.user_id) : user?.id,
    isAdmin: me?.is_admin ?? !!user?.isAdmin,
    modules: me?.modules ?? [],
    accessLevel: me?.access_level ?? "read",
    role: me?.role ?? "member",
    email: me?.email ?? null,
    impersonating: me?.impersonating ?? Boolean(impToken()),
    canRunPipeline: me?.can_run_pipeline ?? false,
    liveEnabled: me?.live_enabled ?? false,
    maxBots: me?.max_bots ?? 1,
    predictionsDailyCap: me?.predictions_daily_cap ?? 10,
    // Which hat the operator is wearing. Defaults to the tenant seat so a
    // pre-hydration render never blanks out a normal user's own portfolio.
    operatingMode: me?.operating_mode ?? "SOVEREIGN_TENANT",
    hidesPersonalData: me?.hides_personal_data ?? false,
    meReady: me != null,
    ready: !!token,
  };
}
